import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import JSAForm, { type JsaSaveIntent } from '../../forms/JSAForm';
import { buildJsaUpdate, isJsaEditable, rejectionReasonText } from '../../lib/jsa';
import { invalidateSafety } from '../../lib/safety-invalidation';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';

/**
 * Edit a draft or rejected JSA, and optionally send it (back) for approval.
 *
 * Before this, a rejected JSA could be resubmitted (api#811) but not changed,
 * so the only resubmit possible sent the rejected content straight back.
 *
 * Reached from the Edit action on the office records row and in the detail
 * modal, in every route tree that mounts the modal (see jsaEditPath).
 *
 * The PUT body is `buildJsaUpdate`'s: only what changed, and the signature
 * rule documented there. The API records every edit with a before/after audit
 * row (api#813), so nothing here writes a second record of it.
 */
export default function JsaEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);

  const query = useQuery({
    queryKey: ['safety-jsa-detail', id],
    queryFn: () => api.safety.getJsa(id!).then((r) => {
      const body = r.data;
      return body && typeof body === 'object' && 'data' in body ? body.data : body;
    }),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const jsa: any = query.data;
  const failure = jsa === undefined ? queryLoadFailure(query) : null;
  const failureError = queryFailureError(query) as any;
  const notFound = failureError?.response?.status === 404 || (query.isSuccess && !jsa);

  const save = async (formData: any, intent: JsaSaveIntent = 'save') => {
    if (!id || !jsa) return;
    const { body } = buildJsaUpdate(jsa, formData);
    const changed = Object.keys(body).length > 0;
    setSaving(true);
    try {
      if (changed) await api.safety.updateJsa(id, body);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to save the JSA');
      setSaving(false);
      return;
    }
    if (intent === 'submit') {
      try {
        await api.safety.submitJsa(id);
      } catch (err: any) {
        invalidateSafety(qc);
        // Both halves, so "saved" is not read as "sent for approval".
        toast.error(`${changed ? 'Changes saved' : 'Nothing changed'}, but submitting for approval failed: ${err?.response?.data?.error || 'the server did not accept it'}`);
        setSaving(false);
        return;
      }
    }
    invalidateSafety(qc);
    toast.success(
      intent === 'submit'
        ? (jsa.status === 'rejected' ? 'JSA resubmitted for approval' : 'JSA submitted for approval')
        : (changed ? 'Changes saved' : 'Nothing to save — no changes were made'),
    );
    setSaving(false);
    navigate(-1);
  };

  let content: React.ReactNode;
  if (notFound) {
    content = <p className="py-12 text-center text-sm text-fieldiq-text">This JSA was not found.</p>;
  } else if (failure) {
    content = <LoadFailureNotice error={failureError} kind={failure} subject="this JSA" />;
  } else if (!jsa) {
    content = <p className="py-12 text-center text-sm text-fieldiq-muted">Loading JSA…</p>;
  } else if (!isJsaEditable(jsa.status)) {
    content = (
      <p className="py-12 text-center text-sm text-fieldiq-text" data-testid="jsa-not-editable">
        {jsa.status === 'submitted'
          ? 'This JSA is waiting for approval, so it can\'t be edited.'
          : jsa.status === 'approved'
            ? 'This JSA is approved and can\'t be edited.'
            : 'This JSA can\'t be edited in its current status.'}
      </p>
    );
  } else {
    content = (
      <>
        {jsa.status === 'rejected' && (
          <div className="mb-4 rounded-lg border border-red-500/20 bg-red-500/10 p-3">
            <p className="text-xs text-fieldiq-text">
              <span className="font-semibold">Rejected:</span> {rejectionReasonText(jsa.rejection_reason ?? jsa.rejectionReason)}
            </p>
          </div>
        )}
        <JSAForm existing={jsa} onSubmit={save} onCancel={() => navigate(-1)} saving={saving} />
      </>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="jsa-edit-page">
      <header className="z-40 bg-fieldiq-dark/95 backdrop-blur border-b border-fieldiq-border px-4 py-3 flex items-center gap-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}>
        <button onClick={() => navigate(-1)} className="text-fieldiq-text">&larr; Back</button>
        <h1 className="text-lg font-bold">Edit JSA</h1>
        {saving && <Loader2 size={18} className="animate-spin text-fieldiq-accent ml-auto" />}
      </header>
      <div className="px-4 py-4">{content}</div>
    </div>
  );
}
